import React, { useCallback, useMemo, useRef } from 'react';
import { CacheEntry, Collection, Loader, StaffDataContext } from './StaffDataContext';

const stableKey = (params: unknown): string => {
  if (params === null || params === undefined) return '∅';
  if (typeof params !== 'object') return JSON.stringify(params);
  const obj = params as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const ordered: Record<string, unknown> = {};
  for (const k of keys) {
    if (obj[k] !== undefined) ordered[k] = obj[k];
  }
  return JSON.stringify(ordered);
};

type CollectionCache = Map<string, CacheEntry>;

interface ProviderProps {
  children: React.ReactNode;
}

export const StaffDataProvider: React.FC<ProviderProps> = ({ children }) => {
  const cachesRef = useRef<Map<Collection, CollectionCache>>(new Map());
  const versionsRef = useRef<Map<Collection, number>>(new Map());
  const listenersRef = useRef<Map<Collection, Set<() => void>>>(new Map());

  const getCache = useCallback((collection: Collection): CollectionCache => {
    let cache = cachesRef.current.get(collection);
    if (!cache) {
      cache = new Map();
      cachesRef.current.set(collection, cache);
    }
    return cache;
  }, []);

  const getVersion = useCallback((collection: Collection): number => {
    return versionsRef.current.get(collection) ?? 0;
  }, []);

  const notify = useCallback((collection: Collection) => {
    const set = listenersRef.current.get(collection);
    if (!set) return;
    for (const listener of set) listener();
  }, []);

  const invalidate = useCallback(
    (collection: Collection) => {
      versionsRef.current.set(collection, getVersion(collection) + 1);
      cachesRef.current.set(collection, new Map());
      notify(collection);
    },
    [getVersion, notify]
  );

  const subscribe = useCallback((collection: Collection, listener: () => void) => {
    let set = listenersRef.current.get(collection);
    if (!set) {
      set = new Set();
      listenersRef.current.set(collection, set);
    }
    set.add(listener);
    return () => {
      set?.delete(listener);
    };
  }, []);

  const read = useCallback(
    <TParams, TResult>(
      collection: Collection,
      params: TParams,
      loader: Loader<TParams, TResult>
    ): TResult => {
      const cache = getCache(collection);
      const key = `${getVersion(collection)}::${stableKey(params)}`;
      const existing = cache.get(key) as CacheEntry<TResult> | undefined;

      if (existing) {
        if (existing.status === 'fulfilled') return existing.value as TResult;
        if (existing.status === 'rejected') throw existing.error;
        throw existing.promise;
      }

      const entry: CacheEntry<TResult> = {
        status: 'pending',
        version: getVersion(collection),
        promise: undefined as unknown as Promise<TResult>
      };
      entry.promise = loader(params).then(
        (value) => {
          entry.status = 'fulfilled';
          entry.value = value;
          return value;
        },
        (error) => {
          entry.status = 'rejected';
          entry.error = error;
          throw error;
        }
      );
      cache.set(key, entry as CacheEntry);
      throw entry.promise;
    },
    [getCache, getVersion]
  );

  const value = useMemo(() => ({ read, invalidate, subscribe }), [read, invalidate, subscribe]);

  return <StaffDataContext.Provider value={value}>{children}</StaffDataContext.Provider>;
};
